import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import decode from "jwt-decode";
import { useTranslation } from "react-i18next";
import lngs from "../../lngs";
import MenuAppBar from "../Menu/Menu";
import PosHeader from "../POS/posHeader";
import LanguageIcon from "@mui/icons-material/Language";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  InputBase,
  Badge,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Button,
  useTheme,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Bell,
  Settings,
  HelpCircle,
  User,
  LogOut,
  Mail,
} from "lucide-react";
import { fetchUnreadCount } from "../../actions/messagingActions";

const Header = ({ toggleOpenDrower }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [posActive, setPosActive] = useState(false);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const profileOpen = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  const dispatch = useDispatch();
  const user = useSelector((state) =>
    JSON.parse(localStorage.getItem("profile"))
  );
  const unreadMessageCount = useSelector(
    (state) => state.messaging?.totalUnreadCount || 0
  );
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage;
  const [isFullscreen, setIsFullscreen] = useState(
    document.fullscreenElement !== null
  );

  const logout = useCallback(() => {
    dispatch({ type: "LOGOUT" });
    history.push(`/${currentLang}/login`);
    localStorage.removeItem("profile");
  }, [dispatch, history, currentLang]);

  const isApp = location.pathname
    .toLowerCase()
    .includes(`/${currentLang}/app`.toLowerCase());

  function toggleFullScreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }

  useEffect(() => {
    setPosActive(location.pathname.toLowerCase() === `/${currentLang}/app/pos`);
  }, [location, currentLang]);

  useEffect(() => {
    if (
      !user &&
      isApp &&
      !location.pathname.toLowerCase().includes(`/${currentLang}/login`)
    ) {
      history.push(`/${currentLang}/login`);
    }
  }, [user, location.pathname, currentLang, history, isApp]);

  // Fetch unread count only once when the component mounts
  useEffect(() => {
    if (user) {
      dispatch(fetchUnreadCount());
    }
    // Intentionally excluding 'user' from the dependency array to prevent infinite loop
    // This effect should only run once when the component mounts
  }, [dispatch]);

  const openLink = (link) => {
    history.push(`${link}`);
  };

  if (!isApp) return <MenuAppBar />;

  const handleLanguageChange = (languageCode, event) => {
    event.preventDefault();
    const currentPath = location.pathname;
    const regex = /^\/[a-z]{2}(?=\/|$)/;
    const newPath = currentPath.replace(regex, `/${languageCode}`);
    history.replace(newPath);
    i18n.changeLanguage(languageCode);
  };

  return (
    <>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          bgcolor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        className="np"
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            height: "44px",
          }}
        >
          {/* Left side - Menu and Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="toggle sidebar"
              onClick={toggleOpenDrower}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>

            {/* Company Logo/Name */}
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                display: { xs: "none", sm: "block" },
                background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginLeft: "5px",
                marginRight: "5px",
              }}
            >
              Quantum Billing ERP
            </Typography>
          </Box>

          {/* Search */}
          <Box
            sx={{
              position: "relative",
              ml: 2,
              mr: 2,
              flexGrow: { xs: 0, md: 0.4 },
              display: { xs: "none", md: "block" },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                display: "flex",
                alignItems: "center",
                height: "100%",
                pl: 1,
                pointerEvents: "none",
                color: "text.secondary",
              }}
            >
              <SearchIcon size={18} />
            </Box>
            <InputBase
              placeholder={t("Search")}
              sx={{
                width: "100%",
                height: "36px",
                fontSize: "0.875rem",
                pl: 4,
                pr: 1,
                bgcolor: "background.default",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                "& .MuiInputBase-input": {
                  padding: "8px 0",
                },
              }}
            />
          </Box>

          {/* Right side - Actions and Profile */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Language Selector */}
            <Tooltip title="Change Language">
              <IconButton
                color="inherit"
                size="medium"
                sx={{ ml: 1 }}
                aria-controls={profileOpen ? "language-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={profileOpen ? "true" : undefined}
              >
                <LanguageIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Notifications">
              <IconButton
                color="inherit"
                size="medium"
                onClick={handleNotificationMenuOpen}
                aria-controls={
                  notificationOpen ? "notification-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={notificationOpen ? "true" : undefined}
                sx={{ ml: 1 }}
              >
                <Badge badgeContent={4} color="error">
                  <Bell size={20} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Messages">
              <IconButton
                color="inherit"
                size="medium"
                sx={{ ml: 1 }}
                onClick={() => {
                  // Fetch the latest unread count when clicking on the messages icon
                  dispatch(fetchUnreadCount());
                  openLink(`/${currentLang}/app/messaging`);
                }}
              >
                <Badge badgeContent={unreadMessageCount} color="error">
                  <Mail size={20} />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Settings">
              <IconButton
                color="inherit"
                size="medium"
                sx={{ ml: 1 }}
                onClick={() => openLink(`/${currentLang}/app/settings`)}
              >
                <Settings size={20} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Help">
              <IconButton color="inherit" size="medium" sx={{ ml: 1 }}>
                <HelpCircle size={20} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Toggle Fullscreen">
              <IconButton
                color="inherit"
                size="medium"
                sx={{ ml: 1 }}
                onClick={toggleFullScreen}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>

            {/* Profile Menu */}
            <Box sx={{ ml: 2 }}>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  aria-controls={profileOpen ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={profileOpen ? "true" : undefined}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "primary.main",
                      fontSize: "0.875rem",
                    }}
                  >
                    {user?.result?.name?.charAt(0)}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>

            {/* Profile Menu Items */}
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={profileOpen}
              onClose={handleProfileMenuClose}
              onClick={handleProfileMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
                  mt: 1.5,
                  width: 200,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={() => openLink(`/${currentLang}/app/profile`)}>
                <ListItemIcon>
                  <User size={18} />
                </ListItemIcon>
                {t("Profile")}
              </MenuItem>
              <MenuItem
                onClick={() => openLink(`/${currentLang}/app/settings`)}
              >
                <ListItemIcon>
                  <Settings size={18} />
                </ListItemIcon>
                {t("Settings")}
              </MenuItem>
              <Divider />
              <MenuItem onClick={logout}>
                <ListItemIcon>
                  <LogOut size={18} />
                </ListItemIcon>
                {t("Logout")}
              </MenuItem>
            </Menu>

            {/* Notification Menu */}
            <Menu
              anchorEl={notificationAnchorEl}
              id="notification-menu"
              open={notificationOpen}
              onClose={handleNotificationMenuClose}
              onClick={handleNotificationMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
                  mt: 1.5,
                  width: 320,
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" component="div" sx={{ mb: 1 }}>
                  {t("Notifications")}
                </Typography>
                <Divider />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("No new notifications")}
                  </Typography>
                </Box>
              </Box>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {posActive && <PosHeader />}
    </>
  );
};

export default Header;
